import React from 'react';
import {
  Box,
  Typography,
  FormControlLabel,
  Switch,
  Divider,
  Button,
  Alert,
  Card,
  CardContent,
} from '@mui/material';
import { useColumnVisibility } from '../../hooks/useColumnVisibility';

interface ColumnConfig {
  id: string;
  label: string;
  description?: string;
}

const PRODUCT_COLUMNS: ColumnConfig[] = [
  { id: 'thumbnail', label: 'Product Image', description: 'Show product thumbnail column' },
  { id: 'name', label: 'Product Name', description: 'Show product name column' },
  { id: 'available_stock', label: 'Available Stock', description: 'Show stock quantity column' },
  { id: 'category', label: 'Category', description: 'Show product category column' },
  { id: 'brand', label: 'Brand', description: 'Show product brand column' },
  { id: 'selling_price', label: 'Selling Price', description: 'Show selling price column' },
  { id: 'cost_price', label: 'Buying Price', description: 'Show cost price column' },
  { id: 'is_active', label: 'Status', description: 'Show active/inactive status column' },
  { id: 'create_time', label: 'Created Date', description: 'Show creation date column' },
  { id: 'clone_action', label: 'Clone Action', description: 'Show clone action column' },
];

export function ProductColumnVisibilitySettings() {
  const { columnVisibility, updateColumnVisibility, resetToDefaults } = useColumnVisibility('products');

  const handleToggle = (columnId: string) => {
    updateColumnVisibility(columnId, !columnVisibility[columnId]);
  };

  const handleReset = () => {
    resetToDefaults();
  };

  const visibleCount = Object.values(columnVisibility).filter(Boolean).length;

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Product Table Columns
        </Typography>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Customize which columns are visible in the products table. Settings are saved locally in your browser.
        </Typography>

        <Alert severity="info" sx={{ mb: 3 }}>
          Currently showing {visibleCount} of {PRODUCT_COLUMNS.length} columns
        </Alert>

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          {PRODUCT_COLUMNS.map((column) => (
            <Box
              sx={{
                flex: {
                  xs: '1 1 100%',
                },
                maxWidth: {
                  sm: 'calc(50% - 8px)',
                  md: 'calc(33.333% - 11px)',
                },
              }}
              key={column.id}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={columnVisibility[column.id] || false}
                    onChange={() => handleToggle(column.id)}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      {column.label}
                    </Typography>
                    {column.description && (
                      <Typography variant="caption" color="text.secondary">
                        {column.description}
                      </Typography>
                    )}
                  </Box>
                }
              />
            </Box>
          ))}
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Changes are automatically saved
          </Typography>
          <Button variant="outlined" onClick={handleReset}>
            Reset to Defaults
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}
